// web-common/src/commands.js — typed-by-test builders over the seam's versioned command JSON
// (D1-DESIGN §2.3). One builder per verb, mirroring harness/v2/api/engine-command.js (its
// header comment is the spec). The suite pins every builder's OUTPUT against the seam's own
// validate() — the contract is enforced by tests, not annotations. Pure; no imports.
//
// The price-then-approve discipline lives HERE, not in screens:
//   quoteCommand(api, cmd)                  — posts { ...cmd, quote_only:true }: NEVER a row.
//   approveCommand(api, cmd, {idempotencyKey}) — posts { ...cmd, confirm:true } with the key.
//
// Idempotency-key rule (repeat it in every screen that spends): mint ONE key per USER INTENT
// (the moment the approve button is first pressed) and reuse it for every retry of that
// intent — the server heals a task-less row on a re-POST with the same key. A NEW intent
// (re-approving after an edit) mints a NEW key.

export const COMMAND_VERSION = 1;

export function newIdemKey() {
  return (globalThis.crypto && crypto.randomUUID) ? crypto.randomUUID()
    : 'k' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function base(verb, payload, { quoteOnly = false, confirm = false, auto = false } = {}) {
  const cmd = { v: COMMAND_VERSION, verb, [verb]: payload };
  if (quoteOnly) cmd.quote_only = true;
  if (confirm) cmd.confirm = true;
  if (auto) cmd.auto = true;
  return cmd;
}

// buildCreate({ genblocks?|brief?|resumeDir?, mode='mock', finish, maxUsd?, ... })
export function buildCreate({ genblocks = null, brief = null, resumeDir = null, mode = 'mock', finish = true, maxUsd = null, quoteOnly = false, confirm = false, auto = false } = {}) {
  const p = { mode };
  if (genblocks != null) p.genblocks = genblocks;
  if (brief != null) p.brief = brief;
  if (resumeDir != null) p.resumeDir = resumeDir;
  if (finish === false) p.finish = false;
  if (maxUsd != null) p.max_usd = Number(maxUsd);
  return base('create', p, { quoteOnly, confirm, auto });
}

// buildEdit({ runDir, instruction?|op?, timestamp?, executeFootage?, mode?, ... })
// An instruction WITHOUT an op is quote/route-only at the enqueue door (the server 400s a
// direct enqueue by design — route first, then approve the returned op).
export function buildEdit({ runDir, instruction = null, op = null, timestamp = null, executeFootage = false, mode = null, quoteOnly = false, confirm = false } = {}) {
  const p = { runDir };
  if (instruction != null) p.instruction = instruction;
  if (op != null) p.op = op;
  if (timestamp != null) p.timestamp = Number(timestamp);
  if (executeFootage) { p.execute = 'footage'; p.mode = mode || 'live'; }
  else if (mode != null) p.mode = mode;
  return base('edit', p, { quoteOnly, confirm });
}

export function buildLocalize({ runDir, langs, tier = null, quoteOnly = false, confirm = false } = {}) {
  const p = { runDir, langs: Array.isArray(langs) ? langs : String(langs || '').split(',').map((s) => s.trim()).filter(Boolean) };
  if (tier != null) p.tier = tier;
  return base('localize', p, { quoteOnly, confirm });
}

export function buildRestyle({ runDir, pack = null, kit = null, keepLook = false, quoteOnly = false, confirm = false } = {}) {
  const p = { runDir };
  if (pack != null) p.pack = pack;
  if (kit != null) p.kit = kit;
  if (keepLook) p.keepLook = true;
  return base('restyle', p, { quoteOnly, confirm });
}

export function buildRegenerate({ runDir, blockId, mode = null, quoteOnly = false, confirm = false } = {}) {
  const p = { runDir, blockId: String(blockId) };
  if (mode != null) p.mode = mode;
  return base('regenerate', p, { quoteOnly, confirm });
}

// ---- the AUTHORING verbs (CREATE-1 §2) — the RAW seam door. The app itself talks to the
// draft routes (web-common/src/drafts.js), which build these server-side; these builders
// pin the seam shapes for the bench, the suites, and API-door examples.

// buildIdeate({ prompt, brief?, settings?, steer?, priorSets?, carry?, ... }) — one
// invocation = ONE idea set (3 cards) = one allowance unit. Steer/more re-invoke with
// priorSets (+ steer); pass the prior set's `carry` back to skip re-research.
export function buildIdeate({ prompt, brief = null, settings = null, steer = null, priorSets = null, carry = null, quoteOnly = false, confirm = false, auto = false } = {}) {
  const p = { prompt: String(prompt || '') };
  if (brief != null) p.brief = brief;
  if (settings != null) p.settings = settings;
  if (steer != null) p.steer = String(steer);
  if (priorSets != null) p.prior_sets = priorSets;
  if (carry != null) p.carry = carry;
  return base('ideate', p, { quoteOnly, confirm, auto });
}

// buildScript({ idea, carry?, brief?, settings?, steer?, priorGenblocks?, ... }) — the
// chosen card (snapshot, as data) -> genblocks + the scene view. Steering re-invokes with
// steer + priorGenblocks (iterate, never a blind rewrite).
export function buildScript({ idea, carry = null, brief = null, settings = null, steer = null, priorGenblocks = null, quoteOnly = false, confirm = false, auto = false } = {}) {
  const p = { idea };
  if (carry != null) p.carry = carry;
  if (brief != null) p.brief = brief;
  if (settings != null) p.settings = settings;
  if (steer != null) p.steer = String(steer);
  if (priorGenblocks != null) p.prior_genblocks = priorGenblocks;
  return base('script', p, { quoteOnly, confirm, auto });
}

// ---- the two-phase discipline over an api client (web-common/src/api.js) ----

// quoteCommand(api, cmd) -> a classified result carrying the quote. Never creates a row.
export async function quoteCommand(api, cmd) {
  return api.post('commands', { ...cmd, quote_only: true });
}

// approveCommand(api, cmd, { idempotencyKey }) -> the classified enqueue answer
// ({ job_id, state, quote, credits_held?, credits_balance? } on kind 'ok').
export async function approveCommand(api, cmd, { idempotencyKey } = {}) {
  const withConfirm = { ...cmd, confirm: true };
  delete withConfirm.quote_only;
  return api.post('commands', withConfirm, { idempotencyKey });
}
